import { toRaw } from 'vue';
import type { AxiosError } from 'axios';
import axiosInstance from '@/services/interceptor.services';
import type {
  ICurrencyBody,
  ICurrencyDetail,
  ICurrencyDetailResponse,
  ICurrencyListResponse,
  ICurrencyUpdateBody,
} from '@/types/currency.types';
import { currency_id, currency_page } from '@/refs/currency.refs';
// import { restaurantId } from "@/refs/common.refs"; À activer si nécessaire

export const CurrencyService = {
  /**
   * Crée une nouvelle devise dans le système.
   *
   * @async
   * @function createCurrency
   * @param {ICurrencyBody} data - Données de la devise à enregistrer (label, name, description).
   * @returns {Promise<any>} La réponse de l'API après la création de la devise.
   * @throws {AxiosError} Lance une erreur si la requête échoue.
   */
  createCurrency: async (data: ICurrencyBody) => {
    try {
      const response = await axiosInstance.post(`currencies/create`, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère la liste paginée des devises disponibles dans le système.
   *
   * Cette fonction construit les paramètres de pagination,
   * puis effectue une requête HTTP GET vers l'API pour obtenir
   * la liste des devises (currencies) enregistrées.
   *
   * @async
   * @function getCurrency
   * @returns {Promise<ICurrencyListResponse>} Données paginées des devises retournées par l'API.
   * @throws {AxiosError} Lance une erreur si la requête échoue.
   */
  getCurrency: async () => {
    try {
      const page = Number(toRaw(currency_page.value));

      const params = new URLSearchParams();
      params.append('pagination', 'true');
      params.append('page', page.toString());
      // params.append("restau", restaurantId.value); // À activer si nécessaire

      const response = await axiosInstance.get<ICurrencyListResponse>(
        `currencies/search?${params.toString()}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Récupère une devise spécifique à partir de son identifiant (code).
   *
   * Cette fonction vérifie d'abord si l'identifiant de la devise (`currency_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP GET à l'API pour récupérer
   * les détails complets de la devise correspondante.
   *
   * @async
   * @function getCurrencyById
   * @returns {Promise<ICurrencyDetailResponse | undefined>}
   *          Retourne les données détaillées de la devise depuis l'API,
   *          ou `undefined` si `currency_id` est null.
   * @throws {AxiosError} Lance une erreur si la requête HTTP échoue.
   */
  getCurrencyById: async () => {
    try {
      if (currency_id.value === null) return;

      const currencyCode = toRaw(currency_id.value.value);

      const response = await axiosInstance.get<ICurrencyDetailResponse>(
        `currencies/get/${currencyCode}`,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Met à jour une devise existante à partir de son identifiant (code).
   *
   * Cette fonction vérifie d'abord si l'identifiant de la devise (`currency_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP PUT à l'API avec les données fournies
   * pour mettre à jour les informations de la devise correspondante.
   *
   * @async
   * @function updateCurrency
   * @param {Partial<ICurrencyUpdateBody>} currencyData - Données partielles de la devise à mettre à jour.
   * @returns {Promise<any | undefined>}
   *          Réponse de l'API après la mise à jour, ou `undefined` si `currency_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête échoue.
   */
  updateCurrency: async (currencyData: Partial<ICurrencyUpdateBody>) => {
    try {
      if (currency_id.value === null) return;

      const currencyCode = toRaw(currency_id.value.value);

      const response = await axiosInstance.put<any>(
        `currencies/update/${currencyCode}`,
        currencyData,
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },

  /**
   * Supprime une devise existante à partir de son identifiant (code).
   *
   * Cette fonction vérifie d'abord si l'identifiant de la devise (`currency_id`)
   * est défini. Si ce n'est pas le cas, elle retourne `undefined`.
   * Sinon, elle envoie une requête HTTP DELETE à l'API afin de supprimer
   * la devise correspondante du système.
   *
   * @async
   * @function deleteCurrencyById
   * @returns {Promise<any | undefined>}
   *          Réponse de l'API après suppression, ou `undefined` si `currency_id` est null.
   * @throws {AxiosError} Lève une erreur si la requête HTTP échoue.
   */
  deleteCurrency: async () => {
    try {
      if (currency_id.value === null) return;

      const currencyCode = toRaw(currency_id.value.value);

      const response = await axiosInstance.delete(
        `currencies/delete/${currencyCode}`,
        {
          headers: { 'Content-Type': 'application/json' },
        },
      );

      return response;
    } catch (error) {
      const err = error as AxiosError;
      return Promise.reject(err);
    }
  },
};
